import {
	afterNextRender,
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	signal,
	untracked,
	viewChild,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { DatabaseViewInstitutionResponse, DatabaseViewSchema } from '../../../api/app-types/database-view.app';
import { provideCategoryApi } from '../../../api/services/categories.service';
import { DATABASE_VIEW_API, provideDatabaseViewApi } from '../../../api/services/database-view.service';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { Paginator } from '../../../core/components/paginator/paginator';
import { ERROR_PROVIDER } from '../../../core/services/error.service';
import { REPRESENTATION_MODE } from '../../../core/services/representation-mode.service';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification } from '../../../shared/notification/notification.factory';
import { LocalStorageService } from '../../../shared/storage/local-storage.service';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { DatabaseColumnSelector } from '../shared/components/database-column-selector/database-column-selector';
import { DatabaseFilterPanelRegistry } from '../shared/components/database-multi-select-filter/database-filter-panel.registry';
import { DatabaseMultiSelectFilter } from '../shared/components/database-multi-select-filter/database-multi-select-filter';
import { DatabaseColumnOption } from '../shared/models/database-column-selection.types';
import { DatabaseSortDirection } from '../shared/models/database-list.types';
import type { DatabaseFilterOption } from '../shared/models/database-view-storage.types';
import { DatabaseViewStorage } from '../shared/services/database-view-storage.service';
import {
	areColumnIdsEqual,
	reconcileColumnIds,
	resetStateForRemovedColumns,
} from '../shared/utils/database-column-selection';
import { runDatabaseCsvExport } from '../shared/utils/database-csv-export';
import {
	createRecordFromKeys,
	filterOptionValues,
	sanitizeFilterOptions,
	toggleFilterSelection,
} from '../shared/utils/database-filter-state';
import {
	buildDatabaseViewInstitutionRequest,
	INSTITUTION_VISIBLE_COLUMNS,
	InstitutionListFilterKey,
	InstitutionListSortField,
} from './database-institutions-list-request';

type FilterKey = InstitutionListFilterKey;
type InstitutionSortField = InstitutionListSortField;
type ColumnKey =
	| 'name'
	| 'schoolNumber'
	| 'category'
	| 'mainInstitution'
	| 'postalCodeCity'
	| 'phone'
	| 'email'
	| 'contactPersons'
	| 'activeClients';

interface InstitutionRow {
	branchId: number;
	mainInstitutionId: number;
	name: string;
	schoolNumber: string;
	category: string;
	mainInstitution: string;
	postalCodeCity: string;
	phone: string;
	email: string;
	contactPersons: string;
	activeClients: string;
}

interface InstitutionColumn {
	key: ColumnKey;
	schemaId: string;
	labelKey: string;
	searchKey?: FilterKey;
	sortField?: InstitutionSortField;
}

interface InstitutionStoredState {
	pageIndex?: number;
	sortField?: InstitutionSortField | null;
	sortDirection?: DatabaseSortDirection;
	filterValues?: Partial<Record<FilterKey, string[]>>;
	filterSearch?: Partial<Record<FilterKey, string>>;
	selectedColumns?: string[];
}

const FILTER_KEYS: FilterKey[] = ['name', 'schoolNumber', 'category', 'mainInstitution', 'postalCodeCity'];
const EMPTY_FILTER_VALUES = createRecordFromKeys(FILTER_KEYS, () => [] as string[]);
const EMPTY_FILTER_SEARCH = createRecordFromKeys(FILTER_KEYS, () => '');
const TAB_ID = 'institutions';
const SCHEMA_VIEW = 'institutions';
const PAGINATOR_PREFERENCE_KEY = 'database-institutions-list';
const CATEGORY_SOURCE_KEYS = ['category'] as const;

const parseInstitutionStoredState = (raw: unknown): InstitutionStoredState | undefined =>
	typeof raw === 'object' && raw !== null ? (raw as InstitutionStoredState) : undefined;

@Component({
	selector: 'lvr-database-institutions-list',
	templateUrl: './database-institutions-list.html',
	styleUrl: './database-institutions-list.scss',
	providers: [provideDatabaseViewApi, provideCategoryApi],
	imports: [
		MatIcon,
		MatIconButton,
		MatMenuModule,
		MatProgressBarModule,
		MatTableModule,
		MatTooltipModule,
		TranslocoPipe,
		Paginator,
		RouterLink,
		LvrButton,
		DatabaseColumnSelector,
		DatabaseMultiSelectFilter,
	],
})
export class DatabaseInstitutionsList {
	private readonly databaseViewApi = inject(DATABASE_VIEW_API);
	private readonly auth = inject(MsalService, { optional: true });
	private readonly storage = inject(LocalStorageService, { optional: true });
	private readonly viewStorage = inject(DatabaseViewStorage);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly errorService = inject(ERROR_PROVIDER);
	private readonly transloco = inject(TranslocoService);
	private readonly environment = inject(ENVIRONMENT);
	private readonly session = inject(SessionManagerService);
	private readonly filterPanels = inject(DatabaseFilterPanelRegistry);
	private readonly destroyRef = inject(DestroyRef);
	private readonly representation = inject(REPRESENTATION_MODE, { optional: true });

	private readonly selector = viewChild(DatabaseColumnSelector);

	private readonly filterOptionsRange = this.environment.pagination.range;
	private readonly filterOptionsOffset = this.environment.pagination.offset;

	private readonly loadingCounter = signal(0);
	/** Filter-option preloads (category) that lock filter, sort, pagination and export while pending. */
	private readonly preloadPending = signal(0);
	/** The newest list request; a new list request or a representation change aborts it. */
	private listAbort: AbortController | null = null;
	private lastRepresentationKey: string | null = null;
	private readonly schema = signal<DatabaseViewSchema | null>(null);
	private readonly schemaReady = signal(false);
	private readonly requestSequence = signal(0);
	private readonly apiRows = signal<DatabaseViewInstitutionResponse[]>([]);
	private readonly totalCount = signal(0);
	private readonly categoryFilterOptions = signal<DatabaseFilterOption[]>([]);
	private readonly categoryFilterValues = computed(() => filterOptionValues(this.categoryFilterOptions()));
	private readonly categoryUnavailable = signal(false);

	public readonly selectedColumnIds = signal<string[]>([...INSTITUTION_VISIBLE_COLUMNS]);
	public readonly applyingColumns = signal(false);
	public readonly pageIndex = signal(0);
	public readonly pageSize = signal(this.environment.defaultPageSize);
	public readonly sortField = signal<InstitutionSortField | null>('name');
	public readonly sortDirection = signal<DatabaseSortDirection>('asc');
	public readonly activeSortField = signal<InstitutionSortField>('name');
	public readonly filterValues = signal<Record<FilterKey, string[]>>({ ...EMPTY_FILTER_VALUES });
	public readonly filterSearch = signal<Record<FilterKey, string>>({ ...EMPTY_FILTER_SEARCH });
	public readonly exporting = signal(false);

	public readonly columns: InstitutionColumn[] = [
		{
			key: 'name',
			schemaId: 'institutionBranch',
			labelKey: 'database_view.institutions.columns.name',
			searchKey: 'name',
			sortField: 'name',
		},
		{
			key: 'schoolNumber',
			schemaId: 'schoolNumber',
			labelKey: 'database_view.institutions.columns.school_number',
			searchKey: 'schoolNumber',
			sortField: 'schoolNumber',
		},
		{
			key: 'category',
			schemaId: 'category',
			labelKey: 'database_view.institutions.columns.category',
			searchKey: 'category',
			sortField: 'category',
		},
		{
			key: 'mainInstitution',
			schemaId: 'mainInstitution',
			labelKey: 'database_view.institutions.columns.main_institution',
			searchKey: 'mainInstitution',
			sortField: 'mainInstitution',
		},
		{
			key: 'postalCodeCity',
			schemaId: 'address',
			labelKey: 'database_view.institutions.columns.postal_city',
			searchKey: 'postalCodeCity',
			sortField: 'postalCodeCity',
		},
		{ key: 'phone', schemaId: 'phoneNumber', labelKey: 'database_view.institutions.columns.phone' },
		{ key: 'email', schemaId: 'email', labelKey: 'database_view.institutions.columns.email' },
		{ key: 'contactPersons', schemaId: 'advisorCount', labelKey: 'database_view.institutions.columns.contact_persons' },
		{
			key: 'activeClients',
			schemaId: 'activeClientsCount',
			labelKey: 'database_view.institutions.columns.active_clients',
		},
	];
	public readonly displayedColumns = computed(() =>
		this.selectedColumnIds().flatMap((schemaId) =>
			this.columns.filter((column) => column.schemaId === schemaId).map((column) => column.key),
		),
	);
	public readonly requestPayload = computed(() =>
		buildDatabaseViewInstitutionRequest(
			{
				filterValues: this.filterValues(),
				filterSearch: this.filterSearch(),
				sortField: this.sortField(),
				sortDirection: this.sortDirection(),
			},
			this.schema(),
			this.selectedColumnIds(),
		),
	);
	public readonly columnOptions = computed<DatabaseColumnOption[]>(() => {
		const schema = this.schema();
		if (!schema) return [];
		return schema.columns.flatMap((schemaColumn) => {
			const column = this.columns.find((candidate) => candidate.schemaId === schemaColumn.id);
			return column
				? [{ schemaId: column.schemaId, renderKey: column.key, label: this.transloco.translate(column.labelKey) }]
				: [];
		});
	});
	public readonly canExport = computed(() => this.schemaReady() && !this.isPageLoading());
	public readonly pageRows = computed(() => this.apiRows().map((x) => this.mapInstitutionToRow(x)));
	public readonly totalItems = computed(() => this.totalCount());
	/** Filter, sort and pagination lock while the schema, a preload or a column change is pending. */
	public readonly controlsLocked = computed(
		() => !this.schemaReady() || this.applyingColumns() || this.preloadPending() > 0,
	);
	/** Column selection also waits for list requests, so the column set matches the shown rows. */
	public readonly columnSelectorLocked = computed(() => this.controlsLocked() || this.loadingCounter() > 0);
	public readonly isPageLoading = computed(() => this.loadingCounter() > 0 || this.controlsLocked());

	public constructor() {
		this.hydrateStateFromStorage();
		this.destroyRef.onDestroy(this.session.onInvalidate(() => this.withdrawForSessionEnd()));
		this.destroyRef.onDestroy(() => this.abortListRequest());
		effect(() => {
			const state = this.representation?.mode();
			const key = state ? `${state.active}|${state.representedUserId ?? ''}` : '';
			untracked(() => {
				if (this.lastRepresentationKey !== null && this.lastRepresentationKey !== key) this.abortListRequest();
				this.lastRepresentationKey = key;
			});
		});
		afterNextRender(() => {
			void this.loadCategoryFilterOptions();
			void this.loadInstitutions();
		});
	}

	/** Drops shown rows and in-flight responses when the session ends, so no stale data stays on screen. */
	private withdrawForSessionEnd() {
		this.abortListRequest();
		this.apiRows.set([]);
		this.totalCount.set(0);
		this.filterPanels.closeActive();
	}

	private abortListRequest() {
		this.requestSequence.update((sequence) => sequence + 1);
		this.listAbort?.abort();
		this.listAbort = null;
	}

	public async export() {
		if (this.exporting() || !this.canExport()) return;
		await runDatabaseCsvExport({
			exporting: this.exporting,
			fetchCsv: () => this.databaseViewApi.getInstitutionsCsv(this.requestPayload()),
			filename: 'institutionen_liste.csv',
			translate: (key) => this.transloco.translate(key),
			notify: (notification) => this.ntfy.addNotification(notification),
		});
	}

	public async applyColumns(candidateIds: readonly string[]) {
		if (this.columnSelectorLocked()) return;
		const nextIds = reconcileColumnIds(this.columnOptions(), candidateIds, this.selectedColumnIds());
		if (!nextIds.length || areColumnIdsEqual(nextIds, this.selectedColumnIds())) return;
		const removedColumns = this.columns.filter((column) => !nextIds.includes(column.schemaId));
		const {
			filterValues: nextFilterValues,
			filterSearch: nextFilterSearch,
			sortField: nextSortField,
			sortDirection: nextSortDirection,
			resetSort,
		} = resetStateForRemovedColumns(
			{
				filterValues: this.filterValues(),
				filterSearch: this.filterSearch(),
				sortField: this.sortField(),
				sortDirection: this.sortDirection(),
			},
			removedColumns.map((column) => ({ filterKey: column.searchKey, sortField: column.sortField })),
			'asc',
		);

		const sequence = this.requestSequence() + 1;
		this.requestSequence.set(sequence);
		this.applyingColumns.set(true);
		try {
			const page = await this.databaseViewApi.getInstitutions(
				buildDatabaseViewInstitutionRequest(
					{
						filterValues: nextFilterValues,
						filterSearch: nextFilterSearch,
						sortField: nextSortField,
						sortDirection: nextSortDirection,
					},
					this.schema(),
					nextIds,
				),
				0,
				this.pageSize(),
			);
			if (sequence !== this.requestSequence()) return;
			this.selectedColumnIds.set([...nextIds]);
			this.filterValues.set(nextFilterValues);
			this.filterSearch.set(nextFilterSearch);
			this.sortField.set(nextSortField);
			this.sortDirection.set(nextSortDirection);
			if (resetSort) this.activeSortField.set('name');
			this.pageIndex.set(0);
			this.apiRows.set(page.entities);
			this.totalCount.set(page.totalEntities);
			this.persistStateToStorage();
			this.selector()?.close();
		} catch {
			this.ntfy.addNotification(createErrorNotification(this.transloco.translate('database_view.columns.apply_error')));
		} finally {
			// Only one column request runs at a time (guarded above), so this request owns the flag.
			this.applyingColumns.set(false);
		}
	}

	public setSort(field: InstitutionSortField, direction: DatabaseSortDirection) {
		if (this.controlsLocked()) return;
		this.sortField.set(field);
		this.sortDirection.set(direction);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadInstitutions();
	}

	public setActiveSortField(field: InstitutionSortField) {
		this.activeSortField.set(field);
	}

	public isSortOptionActive(field: InstitutionSortField, direction: DatabaseSortDirection) {
		return this.sortField() === field && this.sortDirection() === direction;
	}

	public sortIcon(field: InstitutionSortField | null | undefined) {
		if (field !== this.sortField() || !field) return 'swap_vert';
		return this.sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	public sortLabel(direction: DatabaseSortDirection) {
		if (this.activeSortField() === 'schoolNumber') {
			return direction === 'asc'
				? 'database_view.institutions.sort.school_number_ascending'
				: 'database_view.institutions.sort.school_number_descending';
		}
		return direction === 'asc'
			? 'database_view.institutions.sort.ascending'
			: 'database_view.institutions.sort.descending';
	}

	public setFilterSearch(key: FilterKey, value: string) {
		if (this.controlsLocked()) return;
		this.filterSearch.update((s) => ({ ...s, [key]: value }));
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadInstitutions();
	}

	public toggleFilterValue(key: FilterKey, value: string, checked: boolean) {
		if (this.controlsLocked()) return;
		this.filterValues.update((state) => toggleFilterSelection(state, key, value, checked));
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadInstitutions();
	}

	public applyMultiFilterSelection(
		key: FilterKey,
		values: readonly string[],
		filterPanel: Pick<DatabaseMultiSelectFilter, 'closePanel'>,
	) {
		if (this.controlsLocked()) return;
		this.filterValues.update((state) => ({ ...state, [key]: [...values] }));
		this.pageIndex.set(0);
		this.persistStateToStorage();
		filterPanel.closePanel();
		void this.loadInstitutions();
	}

	public onMultiFilterPanelClosed(key: FilterKey) {
		this.filterSearch.update((state) => ({ ...state, [key]: '' }));
	}

	public filterOptions(key: FilterKey) {
		if (key !== 'category') return [];
		return this.categoryFilterValues();
	}

	public isFilterUnavailable(key: FilterKey) {
		return key === 'category' && this.categoryUnavailable();
	}

	public resetAll() {
		if (this.controlsLocked()) return;
		this.filterValues.set({ ...EMPTY_FILTER_VALUES });
		this.filterSearch.set({ ...EMPTY_FILTER_SEARCH });
		this.sortField.set('name');
		this.sortDirection.set('asc');
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadInstitutions();
	}

	public handlePageEvent(event: PageEvent) {
		if (this.controlsLocked()) return;
		this.pageIndex.set(event.pageIndex);
		this.pageSize.set(event.pageSize);
		this.persistStateToStorage();
		void this.loadInstitutions();
	}

	public getCellValue(row: InstitutionRow, key: ColumnKey) {
		return String(row[key] ?? '-');
	}

	public hasBranchRoute(row: InstitutionRow) {
		return row.branchId > 0;
	}

	public branchRoute(row: InstitutionRow) {
		const name = row.name.trim().toLowerCase();
		const mainInstitution = row.mainInstitution.trim().toLowerCase();
		const hasEqualInstitutionNames =
			name !== '' && name !== '-' && mainInstitution !== '' && mainInstitution !== '-' && name === mainInstitution;

		if (hasEqualInstitutionNames && row.mainInstitutionId > 0) {
			return ['/desk/institution', row.mainInstitutionId];
		}

		if (row.mainInstitutionId > 0) {
			return ['/desk/institution', row.mainInstitutionId, 'workplace', row.branchId];
		}
		return ['/desk/institution', row.branchId];
	}

	public hasMainInstitutionRoute(row: InstitutionRow) {
		return row.mainInstitutionId > 0;
	}

	public mainInstitutionRoute(row: InstitutionRow) {
		return ['/desk/institution', row.mainInstitutionId];
	}

	private async loadInstitutions(): Promise<'loaded' | 'stale' | 'failed'> {
		const bypassCache = this.viewStorage.shouldBypassCache(TAB_ID);
		this.listAbort?.abort();
		const controller = new AbortController();
		this.listAbort = controller;
		const sequence = this.requestSequence() + 1;
		this.requestSequence.set(sequence);
		this.loadingCounter.update((count) => count + 1);
		try {
			if (!this.schema()) {
				this.schema.set(
					await this.viewStorage.resolveSchema(
						TAB_ID,
						SCHEMA_VIEW,
						() => this.databaseViewApi.getInstitutionsSchema(),
						bypassCache,
					),
				);
				this.selectedColumnIds.set(
					reconcileColumnIds(this.columnOptions(), this.selectedColumnIds(), INSTITUTION_VISIBLE_COLUMNS),
				);
				this.schemaReady.set(true);
				this.persistStateToStorage();
			}
			const page = await this.databaseViewApi.getInstitutions(
				this.requestPayload(),
				this.pageIndex(),
				this.pageSize(),
				controller.signal,
			);
			if (sequence !== this.requestSequence()) return 'stale';
			this.apiRows.set(page.entities);
			this.totalCount.set(page.totalEntities);
			this.viewStorage.markTabRefreshed(TAB_ID);
			return 'loaded';
		} catch (error) {
			if (controller.signal.aborted || sequence !== this.requestSequence()) return 'stale';
			void this.errorService.handleError(error, true);
			this.ntfy.addNotification(createErrorNotification(this.transloco.translate('database_view.load_error')));
			return 'failed';
		} finally {
			if (this.listAbort === controller) this.listAbort = null;
			this.loadingCounter.update((count) => Math.max(0, count - 1));
		}
	}

	private async loadCategoryFilterOptions() {
		const bypassCache = this.viewStorage.shouldBypassCache(TAB_ID);
		this.preloadPending.update((count) => count + 1);
		try {
			const options = await this.viewStorage.resolveSourceOptions(
				TAB_ID,
				CATEGORY_SOURCE_KEYS,
				async () => {
					const response = await this.databaseViewApi.getInstitutionCategories(
						this.filterOptionsOffset,
						this.filterOptionsRange,
					);
					const values = response.entities.map((x) => String(x ?? '').trim()).filter((x) => x.length > 0);
					return { category: sanitizeFilterOptions(values).map((value) => ({ value, label: value })) };
				},
				bypassCache,
			);
			this.categoryFilterOptions.set(options.category ?? []);
			this.categoryUnavailable.set(options.category === null);
		} finally {
			this.preloadPending.update((count) => Math.max(0, count - 1));
		}
	}

	private mapInstitutionToRow(institution: DatabaseViewInstitutionResponse): InstitutionRow {
		const branchId = institution.institutionBranch?.id ?? 0;
		const mainInstitutionId = institution.mainInstitution?.id ?? 0;
		return {
			branchId,
			mainInstitutionId,
			name: institution.institutionBranch?.text || '-',
			schoolNumber: institution.schoolNumber || '-',
			category: institution.category?.text || '-',
			mainInstitution: institution.mainInstitution?.text || '-',
			postalCodeCity: institution.address?.text || '-',
			phone: institution.phoneNumber || '-',
			email: institution.email || '-',
			contactPersons: String(institution.advisorCount ?? 0),
			activeClients: String(institution.activeClientsCount ?? 0),
		};
	}

	private isSortField(value: string): value is InstitutionSortField {
		return this.columns.some((column) => column.sortField === value);
	}

	private hydrateStateFromStorage() {
		const savedPageSize = this.getSavedPageSize();
		if (savedPageSize) this.pageSize.set(savedPageSize);

		const parsed = this.viewStorage.readListState(TAB_ID, parseInstitutionStoredState);
		if (!parsed) return;

		if (parsed.pageIndex != null) this.pageIndex.set(parsed.pageIndex);
		if (parsed.sortField === null) {
			this.sortField.set(null);
			this.activeSortField.set('name');
		} else if (parsed.sortField && this.isSortField(parsed.sortField)) {
			this.sortField.set(parsed.sortField);
			this.activeSortField.set(parsed.sortField);
		}
		if (parsed.sortDirection === 'asc' || parsed.sortDirection === 'desc') {
			this.sortDirection.set(parsed.sortDirection);
		}
		this.filterValues.set({ ...EMPTY_FILTER_VALUES, ...(parsed.filterValues ?? {}) });
		this.filterSearch.set({ ...EMPTY_FILTER_SEARCH, ...(parsed.filterSearch ?? {}) });
		if (Array.isArray(parsed.selectedColumns)) this.selectedColumnIds.set(parsed.selectedColumns);
	}

	private persistStateToStorage() {
		const state = {
			pageIndex: this.pageIndex(),
			sortField: this.sortField(),
			sortDirection: this.sortDirection(),
			filterValues: this.filterValues(),
			filterSearch: this.filterSearch(),
			selectedColumns: this.selectedColumnIds(),
		};
		this.viewStorage.writeListState(TAB_ID, state);
	}

	private getCurrentUserId(): string | undefined {
		return this.storage?.resolveCurrentUserId(this.auth);
	}

	private getSavedPageSize(): number | undefined {
		const userId = this.getCurrentUserId();
		if (!userId || !this.storage) return undefined;

		const saved = this.storage.getPageSize(userId, PAGINATOR_PREFERENCE_KEY);
		if (!saved || !this.environment.pageSizeOptions.includes(saved)) return undefined;
		return saved;
	}
}
